import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import { Button } from '../Button/index.js';
import 'react-focus-on';
import '@reach/auto-id';
import '@reach/portal';
import '../Transition/index.js';
import { ModalActionBar } from '../Modal/ModalActionBar.js';
import { ModalCloseButton } from '../Modal/ModalCloseButton.js';
import '../Modal/ModalContent.js';
import { Flex } from '../Flex/index.js';
import '../Box/index.js';
import { IconButton } from '../IconButton/index.js';
import { Text } from '../Text/index.js';

/**
 * The DrawerActionBar is a subcomponent of Drawer to provide top level actions for the Drawer such as:
 * - closing
 * - multistep
 * - pagination
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/drawer#draweraction)
 * - [Props](https://blockparty.endpoint.com/components/drawer#draweractionbar-props)
 * <br />
 *
 */
const DrawerActionBar = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    variant = 'default',
    onBackButtonClick,
    onCloseButtonClick,
    onLeftArrowClick,
    onRightArrowClick,
    isPageLabelVisible = true,
    hideCloseButton = false,
    currentPage,
    totalPages,
    step,
    children,
    closeButtonDataTestId = 'drawer-action-bar-close-button',
    ...rest
  } = _ref;
  const isLeftButtonDisabled = currentPage === 1;
  const isRightButtonDisabled = currentPage === totalPages;
  return /*#__PURE__*/React__default.createElement(ModalActionBar, _extends({
    ref: forwardedRef,
    borderBottom: "1px solid",
    borderColor: "divider",
    minHeight: 32 + 24 // padding + close button, ensures the button height is accounted for when hidden
  }, rest), /*#__PURE__*/React__default.createElement(Flex, {
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%"
  }, variant === 'paginated' && /*#__PURE__*/React__default.createElement(Flex, {
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%"
  }, /*#__PURE__*/React__default.createElement(IconButton, {
    color: isLeftButtonDisabled ? 'carbon200' : 'carbon500',
    "data-test-id": "drawer-action-bar-navigation-previous-button",
    isDisabled: isLeftButtonDisabled,
    label: "Previous",
    mr: "space50",
    name: "ArrowChevronLeft",
    variant: "none",
    onClick: onLeftArrowClick
  }), /*#__PURE__*/React__default.createElement(IconButton, {
    color: isRightButtonDisabled ? 'carbon200' : 'carbon500',
    "data-test-id": "drawer-action-bar-navigation-next-button",
    isDisabled: isRightButtonDisabled,
    label: "Next",
    mr: "space50",
    name: "ArrowChevronRight",
    variant: "none",
    onClick: onRightArrowClick
  }), isPageLabelVisible && /*#__PURE__*/React__default.createElement(Text, {
    color: "carbon500"
  }, currentPage, " of ", totalPages)), variant === 'multistep' && step && step > 1 && /*#__PURE__*/React__default.createElement(Button, {
    "data-test-id": "drawer-action-bar-back-button",
    height: "auto",
    iconLeft: "ArrowChevronLeft",
    justifyContent: "flex-start",
    padding: "space0",
    variant: "link",
    width: "100%",
    onClick: onBackButtonClick
  }, "Back"), children, !hideCloseButton && /*#__PURE__*/React__default.createElement(ModalCloseButton, {
    color: "carbon",
    "data-test-id": closeButtonDataTestId,
    onClick: onCloseButtonClick
  })));
});

export { DrawerActionBar };
